import React, { useMemo } from 'react';
import classNames from 'classnames';
import { inject, observer } from 'mobx-react';

import {
  Button,
  Col,
  Form,
  FormCheck,
  FormControl,
  OverlayTrigger,
  Row,
  Tooltip,
} from 'react-bootstrap';

import MadSelect from '../../generic_components/select_component/MadSelect';
import checkPermissions from '../../CheckPermissions';
import ConditionGroupingNote from '../ConditionGroupingNote';
import './signalRow.css';
import './conditionChip.css';
import {
  compactControlHeight,
  compactSelectStyles,
  FORM_COMP_HEIGHT,
} from '../compactControls';
import { moveInList } from './moveInList';
import { signalSentence } from './signalSentence';
import { PROFILES_PERMISSIONS } from '../../../../permissions';
import {
  getVerbSettings,
  getVerbsSettings,
  Verb,
} from '../../../../models/sqlform';
import Store from '../../../../store';
import { computationDisplayName } from '../../../../utils';
import {
  getVerbBoundaries,
  signalConditionPlaceHolder,
  signalPlaceHolder,
} from './utils';
import { SignalDirection } from '../../../../models/signals';
import { action, toJS } from 'mobx';

/**
 * The page's single open-definition slot, as this group sees it.
 *
 * Refusing to leave an edited row lives up there too: only the owner of the
 * slot knows that a row in a different group is mid-edit.
 */
export interface SignalEditSession {
  /** Which definition in THIS group is open, or null if none is. */
  openIndex: number | null;
  /** Leaving was just refused, and the row that refused is in this group. */
  blocked: boolean;
  /** Ask to open `index`, or to close (null). Refused while the row is dirty. */
  requestOpen: (index: number | null) => void;
  /** Report whether the open row still matches how it was found. */
  setDirty: (dirty: boolean) => void;
  /** The open row has been saved or reverted, so the slot is free. */
  release: () => void;
  /** Refuse an action that would write the document. */
  block: () => void;
}

interface SignalsRulesProps {
  store?: Store;
  signalGroupIndex: number;
  /**
   * The definition to mark, when one was reached from the lead sample. Marked
   * rather than scrolled to from here: the group above owns the scroll, because
   * it is the thing that has to open first.
   */
  focusedRule?: number | null;
  /**
   * Collapse each definition to one line until it is opened.
   *
   * Opt in, and only the merged Review page opts in. The old Signals route
   * renders this same component and has to keep rendering what it renders
   * today, since that is the flag off path.
   */
  compact?: boolean;
  /**
   * The one definition open for editing anywhere on the page.
   *
   * Owned above this component rather than here, because the write is one
   * document: an unsaved edit in ANOTHER group would ride along with a Save
   * pressed in this one. One open row for the whole page is what makes a Save
   * button true of the row it sits in.
   *
   * Absent on the unflagged Signals page, which renders every definition open
   * at once and keeps its single Save & test button.
   */
  edit?: SignalEditSession;
  /**
   * Write the group. Passed only by pages that offer a Save control.
   *
   * Two kinds of caller. An edit to a definition is committed by its own Save
   * button, because closing a row used to be the commit and nothing on screen
   * said so. A reorder and a delete call this too: those are finished actions
   * with no form left open to hold a button.
   *
   * The endpoint writes the whole signals document, so this always sends every
   * group. Only one definition can be open at a time and closing one without
   * saving reverts it, which is what keeps "Save" true of the row it sits in:
   * nothing else in the document is pending when it fires.
   */
  onCommit?: () => void | Promise<void>;
}

const verbsSettings = getVerbsSettings({ signals: true });

const SIGNAL_DIRECTIONS_OPTIONS: {
  label: SignalDirection;
  value: SignalDirection;
}[] = [
  { label: '+', value: '+' },
  { label: '-', value: '-' },
];

function SignalsRulesComponent({
  store,
  signalGroupIndex,
  focusedRule,
  compact,
  edit,
  onCommit,
}: SignalsRulesProps) {
  // Read from the page's slot rather than held here. Two open forms in a
  // scannable list is one of the two states this avoids; the other is an
  // unsaved edit in a group you are no longer looking at.
  const openSignal = edit ? edit.openIndex : null;
  const blocked = edit ? edit.blocked : false;

  // A definition reached from the lead sample has to open, not just be marked,
  // or the jump lands on a closed row.
  React.useEffect(() => {
    if (focusedRule !== null && focusedRule !== undefined) {
      edit?.requestOpen(focusedRule);
    }
  }, [focusedRule]);

  // Shared with the override editor, which is the same gesture on another page.
  const controlHeight = compactControlHeight(compact) ?? FORM_COMP_HEIGHT;
  const selectStyles = compactSelectStyles(compact) ?? {
    height: FORM_COMP_HEIGHT,
  };

  // Which condition is open for editing, as `signalIndex.conditionIndex`. One at
  // a time: the point of collapsing them is that the set is readable at a glance,
  // and two open forms is most of the way back to where this started.
  const [openCondition, setOpenCondition] = React.useState<string | null>(null);

  const [dragFrom, setDragFrom] = React.useState<number | null>(null);
  const [dragOver, setDragOver] = React.useState<number | null>(null);
  const { activeModel, formFields, signalsPage, isAllowedToEdit } = store;
  const {
    savingSignals,
    signals,
    signalsFormError,
    validateSignalDescriptionField,
    signalErrorKey,
  } = signalsPage;
  const { signalsV2 } = signals;
  const modelType = activeModel.type;

  /**
   * The definition as it looked when it was opened.
   *
   * OPENING a definition to read it and closing it again is not an edit, and
   * the Save button has to be able to tell the two apart: enabled on every
   * open row it would say a change is pending when none is.
   *
   * Declared here rather than beside the open row because it reads
   * `signalsV2`, which is destructured out of the store above this line.
   */
  const openedAs = React.useRef<string | null>(null);

  const snapshot = (index: number | null) =>
    index === null
      ? null
      : JSON.stringify(toJS(signalsV2[signalGroupIndex]?.signals?.[index]));

  /** Which row `openedAs` was taken from, so a change of row is detectable. */
  const openedFor = React.useRef<number | null>(null);

  // Taken while rendering, not in an effect. An effect runs AFTER the render
  // that opened the row, so `isDirty` below would compare the definition
  // against the previous row's snapshot for one frame and report a change
  // nobody made: every freshly opened row would refuse to close.
  //
  // Taken here rather than at each call site because the row also changes
  // from a jump in the lead sample, a save and a cancel, and that is how the
  // jump path ended up without a snapshot at all.
  if (openedFor.current !== openSignal) {
    openedFor.current = openSignal;
    openedAs.current = snapshot(openSignal);
  }

  /** Whether the open definition differs from how it looked when opened. */
  const isDirty =
    openSignal !== null && snapshot(openSignal) !== openedAs.current;

  // The page decides whether leaving is allowed, and only this group can see
  // whether its own row has been touched. Reported rather than asked for,
  // because the refusal happens on a click in a group that is not this one.
  React.useEffect(() => {
    if (openSignal === null) return;
    edit?.setDirty(isDirty);
  }, [isDirty, openSignal]);

  /** Put the open definition back the way it was found, and close it. */
  const cancelEdit = action(() => {
    if (openSignal !== null && openedAs.current !== null) {
      // Spliced rather than assigned by index, the same reason the reorder
      // splices: the array is the observable every row in this group renders
      // from, and an index assignment on a plain array would revert the data
      // without redrawing the form on top of it.
      signalsV2[signalGroupIndex].signals.splice(
        openSignal,
        1,
        JSON.parse(openedAs.current)
      );
    }
    edit?.release();
  });

  /**
   * Write the definition, and close it only if the write landed.
   *
   * Collapsing is the confirmation that it stored, so a failed write has to
   * keep the row open: the error renders above the card and a collapsed row
   * would leave nothing to correct.
   */
  const saveEdit = async () => {
    if (!onCommit) return;
    await onCommit();
    if (store.signalsPage.signalsError) return;
    edit?.release();
  };

  /**
   * Reorders the group's definitions.
   *
   * There was no way to do this at all: not arrow buttons to be replaced, no
   * control, while the editor stated that order decides which definition wins.
   */
  const moveSignal = action((from: number, to: number) => {
    // A reorder writes the whole document, so an unsaved edit sitting in an
    // open row would go with it. Same refusal as closing the row.
    if (isDirty) {
      edit?.block();
      return;
    }
    const group = store.signalsPage.signals.signalsV2[signalGroupIndex];
    const next = moveInList(group.signals.slice(), from, to);
    // A reorder that changed the count would silently change what the model
    // does, since only the first matching definition is displayed.
    if (next.length !== group.signals.length) return;
    // Spliced in place rather than reassigned: the array is the observable that
    // every row in this group renders from.
    group.signals.splice(0, group.signals.length, ...next);
    // The open row does not follow the definition to its new position, so the
    // slot is given back rather than left pointing at whatever moved into it.
    edit?.release();
    // Order decides which definition wins, so a reorder is a change in its own
    // right and commits whether or not a row was being edited. Any edit pending
    // in the open row is already in the observable, so this persists it too.
    if (onCommit) onCommit();
  });

  const dropOn = (target: number) => {
    if (dragFrom === null) return;
    // A drop lands before the target, so dragging downwards passes its own slot.
    moveSignal(dragFrom, dragFrom < target ? target + 1 : target);
    setDragFrom(null);
    setDragOver(null);
  };

  const formFieldOptions = useMemo(
    () =>
      (formFields || []).map(({ subject }) => ({
        label: computationDisplayName(subject),
        value: subject,
      })),
    [formFields]
  );

  const signalDescriptionOptions = useMemo(
    () => [{ label: 'null', value: 'null' }, ...formFieldOptions],
    [formFieldOptions]
  );

  const signalConditionFieldOptions = useMemo(
    () =>
      // add 'undefined' as first element of field list, to serve as default value
      // when field wasn't correctly unparsed.
      [{ label: 'undefined', value: 'undefined' }, ...formFieldOptions],
    [formFieldOptions]
  );

  const verbsSettingsOptions: {
    label: Verb;
    value: Verb;
  }[] = useMemo(
    () =>
      verbsSettings
        .filter(({ modelTypes }) =>
          modelTypes.includes(modelType === 'mqa' ? 'mqa' : 'customer_fit')
        )
        .map(({ value }) => ({
          label: value,
          value,
        })),
    [modelType]
  );

  const conditionallyRefreshValueField = (
    signalIndex: number,
    conditionIndex: number
  ) => {
    const { values, verb } =
      signalsV2[signalGroupIndex].signals[signalIndex].conditions[
        conditionIndex
      ];

    // if length of current values is greater than allowed max with verb, clear
    if (values.length > getVerbBoundaries(verb).max) {
      signalsV2[signalGroupIndex].signals[signalIndex].conditions[
        conditionIndex
      ].values = [''];
    }
  };

  if (!formFields) return null;

  return (
    <Form>
      {signalsV2[signalGroupIndex].signals.map((signal, signalIndex) => {
        const isAndConditions = signal.conditions.find(
          // don't take the direction of first condition into account
          (condition, index) => condition.direction === 'AND' && index !== 0
        );
        const isOrConditions = signal.conditions.find(
          (condition, index) => condition.direction === 'OR' && index !== 0
        );
        const displayOrButton =
          (!isAndConditions && isOrConditions) ||
          signal.conditions.length === 1;
        const displayAndButton =
          (isAndConditions && !isOrConditions) ||
          signal.conditions.length === 1;

        // get detected error on a field for current signal
        const signalError = (field: string) =>
          signalsFormError[
            signalErrorKey({ field, signalGroupIndex, signalIndex })
          ];

        const isOpen = !compact || openSignal === signalIndex;

        return (
          <div
            key={`signal_group_${signalGroupIndex}_row_${signalIndex}`}
            id={`signal_${signalGroupIndex}_${signalIndex}`}
            // The padded row belongs to the compact reading, where signals are
            // scanned as a list. The full editor had a bare div and keeps one:
            // `.signal-row` shifts every definition by 4px, which is a change
            // to a page the revamp is switched off for.
            className={
              compact
                ? focusedRule === signalIndex
                  ? 'signal-row signal-row--found'
                  : 'signal-row'
                : undefined
            }
          >
            {/* The scannable line. Position decides which definition wins, so
                the number is part of the reading and not decoration. */}
            {compact && (
              <div
                className={
                  dragOver === signalIndex
                    ? 'signal-head signal-head--drop'
                    : 'signal-head'
                }
                onDragOver={(event) => {
                  if (dragFrom === null) return;
                  event.preventDefault();
                  setDragOver(signalIndex);
                }}
                onDragLeave={() => setDragOver(null)}
                onDrop={(event) => {
                  event.preventDefault();
                  dropOn(signalIndex);
                }}
              >
                {/* Order decides which definition wins, so reordering is a drag
                    rather than a pair of arrows. The grip stays keyboard
                    reachable: focus it and use the arrow keys, which is the same
                    affordance without the clutter. */}
                <button
                  type="button"
                  className="signal-grip"
                  draggable={isAllowedToEdit && !savingSignals}
                  disabled={!isAllowedToEdit || savingSignals}
                  aria-label={`Reorder definition ${
                    signalIndex + 1
                  }. Use arrow up and arrow down.`}
                  title="Drag to reorder"
                  onDragStart={() => setDragFrom(signalIndex)}
                  onDragEnd={() => {
                    setDragFrom(null);
                    setDragOver(null);
                  }}
                  onKeyDown={(event) => {
                    const step =
                      event.key === 'ArrowUp'
                        ? -1
                        : event.key === 'ArrowDown'
                        ? 1
                        : 0;
                    if (!step) return;
                    event.preventDefault();
                    moveSignal(signalIndex, signalIndex + step);
                  }}
                >
                  <svg width="10" height="14" viewBox="0 0 10 14" aria-hidden>
                    <g fill="currentColor">
                      <circle cx="2" cy="3" r="1.2" />
                      <circle cx="8" cy="3" r="1.2" />
                      <circle cx="2" cy="7" r="1.2" />
                      <circle cx="8" cy="7" r="1.2" />
                      <circle cx="2" cy="11" r="1.2" />
                      <circle cx="8" cy="11" r="1.2" />
                    </g>
                  </svg>
                </button>
                <button
                  type="button"
                  className="signal-line"
                  // Closing this one, or opening another over it, both leave
                  // the edit in progress, so both are refused while it is
                  // unsaved. Save and Cancel are the two ways out and they sit
                  // at the bottom of the open row.
                  onClick={() =>
                    edit?.requestOpen(isOpen ? null : signalIndex)
                  }
                  aria-expanded={isOpen}
                >
                  <span className="signal-line-chevron" aria-hidden="true">
                    {isOpen ? '▼' : '►'}
                  </span>
                  <span className="signal-line-ord">{signalIndex + 1}</span>
                  <span
                    className={
                      signal.direction === '-'
                        ? 'signal-line-sign signal-line-sign--neg'
                        : 'signal-line-sign'
                    }
                  >
                    {signal.direction}
                  </span>
                  {/* `description.field` holds the sentence Sales reads and
                      `description.value` holds the attribute appended to it. The
                      names are the wrong way round, which is worth stating here
                      because reading them the other way shows the column name
                      where the label belongs. */}
                  <span className="signal-line-text">
                    {signal.description?.field || 'No display text'}
                  </span>
                  <span className="signal-line-when">
                    {signalSentence(signal.conditions) || 'no conditions'}
                  </span>
                </button>
                {isOpen && isDirty && (
                  <span className="signal-unsaved">Unsaved</span>
                )}
              </div>
            )}
            <div hidden={!isOpen}>
              <Row className="mb-4 my-auto">
                <Col xs={1} className="my-auto text-center">
                  {!compact && <h6>Display</h6>}
                </Col>
                <Col xs={1}>
                  <MadSelect
                    controlStyles={selectStyles}
                    isDisabled={!isAllowedToEdit || savingSignals}
                    isSearchable={false}
                    options={SIGNAL_DIRECTIONS_OPTIONS}
                    value={{
                      label: signal.direction,
                      value: signal.direction,
                    }}
                    onChange={action(({ value }) => {
                      signalsV2[signalGroupIndex].signals[
                        signalIndex
                      ].direction = value;
                    })}
                  />
                </Col>

                <Col xs={3}>
                  <FormControl
                    isInvalid={!!signalError('description.field')}
                    type="text"
                    style={{ height: controlHeight }}
                    name="description_field"
                    disabled={!isAllowedToEdit || savingSignals}
                    value={signal.description.field}
                    placeholder="Signal will display as this..."
                    onChange={action(
                      (event: React.ChangeEvent<HTMLInputElement>) => {
                        const description = event.target.value;

                        validateSignalDescriptionField({
                          description,
                          signalGroupIndex,
                          signalIndex,
                        });

                        signalsV2[signalGroupIndex].signals[
                          signalIndex
                        ].description.field = description;
                      }
                    )}
                  />
                  {!!signalError('description.field') && (
                    <div className="tw-mt-1 tw-text-red-500">
                      {signalError('description.field')}
                    </div>
                  )}
                </Col>
                <Col xs={3}>
                  <MadSelect
                    controlStyles={selectStyles}
                    isDisabled={!isAllowedToEdit || savingSignals}
                    options={signalDescriptionOptions}
                    value={signalDescriptionOptions.find(
                      ({ value }) =>
                        value === (signal.description.value || 'null')
                    )}
                    onChange={action(({ value }) => {
                      signalsV2[signalGroupIndex].signals[
                        signalIndex
                      ].description.value = value;
                    })}
                  />
                </Col>
                <Col xs={1} className="text-end my-auto">
                  <h6>Priority</h6>
                </Col>
                <Col xs={1}>
                  <FormControl
                    type="number"
                    style={{ height: controlHeight }}
                    min={0}
                    max={100}
                    name="priority"
                    disabled={!isAllowedToEdit || savingSignals}
                    value={signal.priority}
                    onChange={action(
                      (event: React.ChangeEvent<HTMLInputElement>) => {
                        signalsV2[signalGroupIndex].signals[
                          signalIndex
                        ].priority = Number(event.target.value);
                      }
                    )}
                  />
                </Col>
                <Col xs={1}>
                  {checkPermissions(
                    PROFILES_PERMISSIONS.ARCHITECT,

                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip">
                          Remove the signal display
                        </Tooltip>
                      }
                    >
                      <Button
                        variant={compact ? 'outline-secondary' : 'danger'}
                        size={compact ? 'sm' : undefined}
                        onClick={action(() => {
                          // Deleting writes the document. An unsaved edit in
                          // another row must not ride along with it.
                          if (isDirty && openSignal !== signalIndex) {
                            edit?.block();
                            return;
                          }
                          signalsV2[signalGroupIndex].signals.splice(
                            signalIndex,
                            1
                          );
                          // Every row below this one just changed index, so the
                          // open row and its snapshot no longer refer to what
                          // they were taken from.
                          edit?.release();
                          if (onCommit) onCommit();
                        })}
                        disabled={!isAllowedToEdit || savingSignals}
                      >
                        <i aria-hidden className="fas fa-times"></i>
                      </Button>
                    </OverlayTrigger>
                  )}
                </Col>
              </Row>
              <hr className="semi-divider" />
              {/* Explains how a mixed and/or list actually groups. It belongs
                  with the one-line reading the compact editor introduced; the
                  full editor showed the conditions as a list and said nothing,
                  so a tenant without the revamp gets no new paragraph. */}
              {compact && (
                <ConditionGroupingNote conditions={signal.conditions} />
              )}
              {signal.conditions.map((condition, conditionIndex) => {
                const conditionVerbSettings = getVerbSettings(condition.verb);
                const conditionKey = `${signalIndex}.${conditionIndex}`;
                // Collapsed, a condition is a sentence. Open, it is the form
                // that ships. The controls are not replaced, because a row of
                // selects is the right thing for editing one condition and the
                // wrong thing for reading five, and only the reading was broken.
                const conditionOpen = compact
                  ? openCondition === conditionKey
                  : true;

                return (
                  <React.Fragment
                    key={`signal_group_${signalGroupIndex}_row_${signalIndex}_condition_${conditionIndex}`}
                  >
                    {/* The sentence stays while the form is open, so the
                        selects have something to update in front of you and the
                        chip remains the way back. Hidden on open it would be a
                        dead end: there would be nothing left to click. */}
                    {compact && (
                      <div className="cond-line">
                        {conditionIndex > 0 && (
                          <span className="cond-join">
                            {condition.direction || 'AND'}
                          </span>
                        )}
                        <button
                          type="button"
                          className={
                            conditionOpen
                              ? 'cond-chip cond-chip--on'
                              : 'cond-chip'
                          }
                          aria-expanded={conditionOpen}
                          onClick={() =>
                            setOpenCondition(
                              conditionOpen ? null : conditionKey
                            )
                          }
                          title={
                            conditionOpen
                              ? 'Done editing'
                              : 'Edit this condition'
                          }
                        >
                          <span className="cond-field">
                            {condition.field || 'no attribute'}
                          </span>
                          <i className="cond-verb">{condition.verb}</i>
                          {!!condition.values?.filter(Boolean).length && (
                            <b className="cond-values">
                              {condition.values.filter(Boolean).join(', ')}
                            </b>
                          )}
                        </button>
                      </div>
                    )}
                    {conditionOpen && (
                      <Row className="mb-3">
                        {conditionIndex === 0 && (
                          <Col xs={1} className="text-center">
                            {!compact && <h6>Definition</h6>}
                          </Col>
                        )}
                        {conditionIndex > 0 && (
                          <Col xs={1} className="pt-1 text-end">
                            {condition.direction || 'AND'}
                          </Col>
                        )}
                        <Col xs={3}>
                          <MadSelect
                            controlStyles={selectStyles}
                            isDisabled={!isAllowedToEdit || savingSignals}
                            options={signalConditionFieldOptions}
                            value={signalConditionFieldOptions.find(
                              ({ value }) => value === condition.field
                            )}
                            onChange={action(({ value }) => {
                              signalsV2[signalGroupIndex].signals[
                                signalIndex
                              ].conditions[conditionIndex].field = value;
                            })}
                          />
                        </Col>
                        <Col xs={2}>
                          <Row>
                            <Col xs={12}>
                              <MadSelect
                                controlStyles={selectStyles}
                                isDisabled={!isAllowedToEdit || savingSignals}
                                options={verbsSettingsOptions}
                                value={{
                                  label: condition.verb,
                                  value: condition.verb,
                                }}
                                onChange={action(({ value }) => {
                                  signalsV2[signalGroupIndex].signals[
                                    signalIndex
                                  ].conditions[conditionIndex].verb = value;

                                  const verbSettings = getVerbSettings(value);
                                  // Reset lower to false, if the verb doesn't apply to strings
                                  if (
                                    verbSettings.characteristics.types.includes(
                                      'string'
                                    )
                                  ) {
                                    signalsV2[signalGroupIndex].signals[
                                      signalIndex
                                    ].conditions[conditionIndex].lower = false;
                                  }
                                  conditionallyRefreshValueField(
                                    signalIndex,
                                    conditionIndex
                                  );
                                })}
                              />
                              {conditionVerbSettings.characteristics.types.includes(
                                'string'
                              ) && (
                                <Col xs={12}>
                                  <FormCheck
                                    type="checkbox"
                                    name="lower_field"
                                    disabled={!isAllowedToEdit || savingSignals}
                                    checked={
                                      signal.conditions[conditionIndex].lower
                                    }
                                    onChange={action(() => {
                                      signalsV2[signalGroupIndex].signals[
                                        signalIndex
                                      ].conditions[conditionIndex].lower =
                                        !signal.conditions[conditionIndex]
                                          .lower;
                                    })}
                                    label="Case-insensitive"
                                  />
                                </Col>
                              )}
                            </Col>
                          </Row>
                        </Col>
                        {condition.verb !== 'is known' &&
                          condition.verb !== 'is unknown' && (
                            <React.Fragment>
                              <Col xs={4}>
                                {condition.values.map((value, valueIndex) => (
                                  <Row
                                    className={classNames('gx-1', {
                                      'mt-1': valueIndex,
                                    })}
                                    key={`signal_group_${signalGroupIndex}_row_${signalIndex}_condition_${conditionIndex}_value_${valueIndex}`}
                                  >
                                    <Col xs={8}>
                                      <FormControl
                                        type="text"
                                        name="value"
                                        style={{ height: controlHeight }}
                                        disabled={
                                          !isAllowedToEdit || savingSignals
                                        }
                                        value={value}
                                        placeholder="Enter value..."
                                        onChange={action(
                                          (
                                            event: React.ChangeEvent<HTMLInputElement>
                                          ) => {
                                            signalsV2[signalGroupIndex].signals[
                                              signalIndex
                                            ].conditions[conditionIndex].values[
                                              valueIndex
                                            ] = event.target.value;
                                          }
                                        )}
                                      />
                                    </Col>
                                    <Col xs={4}>
                                      {condition.values.length &&
                                        valueIndex ===
                                          condition.values.length - 1 &&
                                        condition.values.length <
                                          getVerbBoundaries(condition.verb)
                                            .max && (
                                          <OverlayTrigger
                                            placement="left"
                                            overlay={
                                              <Tooltip id="tooltip">
                                                Add a value
                                              </Tooltip>
                                            }
                                          >
                                            <Button
                                              variant="primary"
                                              disabled={
                                                !isAllowedToEdit ||
                                                savingSignals
                                              }
                                              onClick={action(() =>
                                                signalsV2[
                                                  signalGroupIndex
                                                ].signals[
                                                  signalIndex
                                                ].conditions[
                                                  conditionIndex
                                                ].values.push('')
                                              )}
                                              className="me-1"
                                            >
                                              +
                                            </Button>
                                          </OverlayTrigger>
                                        )}
                                      {condition.values.length >
                                        getVerbBoundaries(condition.verb)
                                          .min && (
                                        <OverlayTrigger
                                          placement="left"
                                          overlay={
                                            <Tooltip id="tooltip">
                                              Remove the value
                                            </Tooltip>
                                          }
                                        >
                                          <Button
                                            variant={
                                              compact
                                                ? 'outline-secondary'
                                                : 'danger'
                                            }
                                            size={compact ? 'sm' : undefined}
                                            disabled={
                                              !isAllowedToEdit || savingSignals
                                            }
                                            onClick={action(() =>
                                              signalsV2[
                                                signalGroupIndex
                                              ].signals[signalIndex].conditions[
                                                conditionIndex
                                              ].values.splice(valueIndex, 1)
                                            )}
                                          >
                                            -
                                          </Button>
                                        </OverlayTrigger>
                                      )}
                                      {(condition.values.length === 1 ||
                                        valueIndex ===
                                          condition.values.length - 1) &&
                                        signal.conditions.length > 1 && (
                                          <OverlayTrigger
                                            placement="left"
                                            overlay={
                                              <Tooltip id="tooltip">
                                                Remove sub-condition
                                              </Tooltip>
                                            }
                                          >
                                            <Button
                                              variant="outline-danger"
                                              disabled={
                                                !isAllowedToEdit ||
                                                savingSignals
                                              }
                                              onClick={action(() =>
                                                signalsV2[
                                                  signalGroupIndex
                                                ].signals[
                                                  signalIndex
                                                ].conditions.splice(
                                                  conditionIndex,
                                                  1
                                                )
                                              )}
                                            >
                                              <i
                                                aria-hidden
                                                className="fas fa-times"
                                              ></i>
                                            </Button>
                                          </OverlayTrigger>
                                        )}
                                    </Col>
                                  </Row>
                                ))}
                              </Col>
                            </React.Fragment>
                          )}
                      </Row>
                    )}
                  </React.Fragment>
                );
              })}

              <Col>
                <div className="ms-5 ps-5 mb-3">
                  {displayOrButton && (
                    <Button
                      variant={compact ? 'light' : 'outline-primary'}
                      size={compact ? 'sm' : undefined}
                      onClick={action(() =>
                        signalsV2[signalGroupIndex].signals[
                          signalIndex
                        ].conditions.push(
                          signalConditionPlaceHolder(formFields, 'OR')
                        )
                      )}
                      disabled={!isAllowedToEdit || savingSignals}
                      className="me-1"
                    >
                      + OR
                    </Button>
                  )}
                  {displayAndButton && (
                    <Button
                      variant={compact ? 'light' : 'outline-primary'}
                      size={compact ? 'sm' : undefined}
                      disabled={!isAllowedToEdit || savingSignals}
                      onClick={action(() =>
                        signalsV2[signalGroupIndex].signals[
                          signalIndex
                        ].conditions.push(
                          signalConditionPlaceHolder(formFields, 'AND')
                        )
                      )}
                    >
                      + AND
                    </Button>
                  )}
                </div>
              </Col>

              {edit && onCommit && (
                <div className="signal-commit">
                  {blocked && (
                    <span className="signal-commit-note">
                      Save or cancel this definition first.
                    </span>
                  )}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={cancelEdit}
                    disabled={savingSignals}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={saveEdit}
                    /* Disabled on an unchanged definition rather than hidden.
                       A button that is missing until you type reads as the
                       page not having one, which is the state this replaces. */
                    disabled={!isAllowedToEdit || savingSignals || !isDirty}
                  >
                    {savingSignals ? 'Saving...' : 'Save definition'}
                  </Button>
                </div>
              )}
              {!compact && <hr></hr>}
            </div>
          </div>
        );
      })}

      <Row className="text-start">
        <Col xs={2}>
          <Button
            className="me-1"
            onClick={action(() =>
              signalsV2[signalGroupIndex].signals.push(
                signalPlaceHolder(formFields)
              )
            )}
            disabled={!isAllowedToEdit || savingSignals}
          >
            Add signal variable
          </Button>
        </Col>
        {/* The page above already states this once. Repeating it per group was
            three copies of the same sentence on screen. */}
        {!compact && (
          <Col className="my-auto">
            <p className="m-0 fst-italic">
              The order of definitions is important as only the first signal
              definition verified in a group will be displayed.
            </p>
          </Col>
        )}
        {compact && <Col />}
        <Col xs={2} className="text-end">
          <Button
            variant={compact ? 'outline-secondary' : 'danger'}
            size={compact ? 'sm' : undefined}
            onClick={action(() => {
              // Removing a group writes the document, so an unsaved edit
              // anywhere in it would be written too.
              if (isDirty) {
                edit?.block();
                return;
              }
              signalsV2.splice(signalGroupIndex, 1);
              edit?.release();
              if (onCommit) onCommit();
            })}
            disabled={!isAllowedToEdit || savingSignals}
          >
            Remove signal group
          </Button>
        </Col>
      </Row>
    </Form>
  );
}

export default inject('store')(observer(SignalsRulesComponent));
